import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { QuizApiClient } from '@/infrastructure/quiz/QuizApiClient';
import { useAppTranslation } from '@/i18n/useAppTranslation';
import { quizDifficultyLabel } from '@/i18n/quizDifficulty';
import { PLACEHOLDER_THUMB, getQuizTypeUi } from '@/presentation/quiz/publicQuizUiResources';
import { formatApiErrorMessage } from '@/utils/formatApiErrorMessage';

export default function PublicQuizDetailPage() {
  const { t } = useAppTranslation();
  const { code } = useParams();
  const api = React.useMemo(() => new QuizApiClient(), []);
  const [loading, setLoading] = React.useState(true);
  const [quiz, setQuiz] = React.useState(null);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    let mounted = true;
    if (!code) return undefined;
    setLoading(true);
    api
      .getPublicQuizByCode(code)
      .then((data) => {
        if (!mounted) return;
        setQuiz(data);
      })
      .catch((e) => {
        if (!mounted) return;
        setError(formatApiErrorMessage(e?.message, t) || t('errors.loadPublicQuizDetail'));
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [api, code, t]);

  const meta = getQuizTypeUi(quiz?.quiz_type);
  const qCount = Array.isArray(quiz?.questions) ? quiz.questions.length : quiz?.question_count || 0;

  return (
    <main className="mx-auto max-w-4xl space-y-4 p-6">
      <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm font-semibold text-cyan-700 hover:underline">
        <span className="material-symbols-outlined !text-base">arrow_back</span>
        {t('publicQuiz.backDashboard')}
      </Link>
      {loading ? <p className="rounded-xl bg-white p-4 text-sm text-slate-600">{t('publicQuiz.loading')}</p> : null}
      {error ? <p className="rounded-xl bg-rose-50 p-4 text-sm font-semibold text-rose-700">{error}</p> : null}
      {quiz ? (
        <section className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold ${meta.tone}`}>
              <span className="material-symbols-outlined !text-sm">{meta.icon}</span>
            </span>
            <span className="rounded-full bg-cyan-100 px-3 py-1 text-xs font-bold text-cyan-700">
              {quizDifficultyLabel(t, quiz.difficulty)}
            </span>
          </div>
          <h1 className="font-headline text-3xl font-extrabold text-slate-900">{quiz.title}</h1>
          <p className="mt-2 text-slate-600">{quiz.description || t('publicQuiz.defaultDesc')}</p>
          <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
            <img
              src={PLACEHOLDER_THUMB}
              alt=""
              className="aspect-[16/7] w-full object-cover opacity-80"
            />
          </div>
          <div className="mt-4 grid gap-2 text-sm text-slate-600 md:grid-cols-3">
            <p>{t('publicQuiz.questionsCount', { count: qCount })}</p>
            <p>{t('publicQuiz.quizType', { label: meta.label })}</p>
            <p>{t('publicQuiz.shareCode', { code: quiz.share_code })}</p>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to={`/play/${quiz.share_code}`}
              className="rounded-xl bg-cyan-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-cyan-700"
            >
              {t('publicQuiz.playNow')}
            </Link>
            <Link
              to="/quizzes/manage"
              className="rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              {t('publicQuiz.moreQuizzes')}
            </Link>
          </div>
        </section>
      ) : null}
    </main>
  );
}
